import { useState, useEffect } from 'react';

import styled from 'styled-components';
import moment from 'moment';
import Lottie from 'lottie-react';

import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';
import { useLanguage } from '../../../Context/LanguageContext';

import LoadingAnimation from '../../../Animation/NewLoading.json';
import { ReactComponent as CrownSvg } from '../../../image/StatisticsCrown.svg';
import { ReactComponent as AddCardSvg } from '../../../image/StatisticsAddCard.svg';
import { ReactComponent as CurrencyExchangeSvg } from '../../../image/StatisticsCurrencyExchange.svg';

// 연령대 index(0=10대 이하 ~ 9=90대) → 번역 키 //
const AGE_LABEL_KEYS = ['ageUnder10', 'age10s', 'age20s', 'age30s', 'age40s', 'age50s', 'age60s', 'age70s', 'age80s', 'age90s'];
// 연령대 index → 스택 바 세그먼트 색상 키 (라이트: 연령 높을수록 연한 그린 / 다크: 연령 높을수록 진한 그린) //
const AGE_SEGMENT_COLOR_KEYS_LIGHT = ['brand500', 'brand400', 'brand350', 'brand300', 'brand250', 'brand200', 'brand150', 'brand100', 'brand100', 'brand100'];
const AGE_SEGMENT_COLOR_KEYS_DARK = ['brand500', 'brand550', 'brand600', 'brand650', 'brand700', 'brand750', 'brand800', 'brand850', 'brand900', 'brand900'];
// 순위(rank) 0~2 뱃지 색상 키 (라이트: 밝은 그린 / 다크: 진한 그린) //
const RANK_BADGE_COLOR_KEYS_LIGHT = ['brand500', 'brand400', 'brand300'];
const RANK_BADGE_COLOR_KEYS_DARK = ['brand500', 'brand600', 'brand700'];

export default function MembershipPreferenceBox(props) {
  const {
    calcDateText,
    startDate,
    endDate,
    allMembers,
    salesData,
    isLoadingAllMember,
    isOpenPartnerGymSales,
    isMembershipPreferenceLoading,
    setIsMembershipPreferenceLoading,
  } = props;

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

  // 라이트/다크 모드별 그린 팔레트 선택 (라이트는 피그마 디자인 기준 밝은 그린) //
  const AGE_SEGMENT_COLOR_KEYS = isDark ? AGE_SEGMENT_COLOR_KEYS_DARK : AGE_SEGMENT_COLOR_KEYS_LIGHT;
  const RANK_BADGE_COLOR_KEYS = isDark ? RANK_BADGE_COLOR_KEYS_DARK : RANK_BADGE_COLOR_KEYS_LIGHT;

  const [rankings, setRankings] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);

  const [topCards, setTopCards] = useState(null);
  const [avgReRegRate, setAvgReRegRate] = useState(0);

  const [tooltipRow, setTooltipRow] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  // 회원권 선호도 데이터 가공 (가입 회원 = 산정기간 내 등록한 신규+재등록 회원) //
  useEffect(() => {
    if (isLoadingAllMember) return;

    const year = parseInt(moment().format('YYYY'));

    // 출생연도 → 연령대 인덱스 (구간을 벗어나면 -1) //
    function getAgeGroupIndex(birthYear) {
      const age = year - birthYear;

      if (age < 0 || age > 98) return -1;

      return Math.floor((age + 1) / 10);
    }

    // 회원권별 집계: 산정기간 내 등록한 회원을 회원권별로 그룹핑 (회원 1명은 회원권당 1회만 카운트) //
    const membershipMap = new Map();

    for (const member of allMembers) {
      if (!member.birthDay || !Array.isArray(member.usages)) continue;

      const ageIndex = getAgeGroupIndex(parseInt(member.birthDay.slice(0, 4)));

      if (ageIndex === -1) continue;

      const countedMemberships = new Set();

      for (let j = 0; j < member.usages.length; j++) {
        const usage = member.usages[j];

        if (!usage.memberShipId || !usage.memberShipName) continue;
        if (!(startDate.isSameOrBefore(usage.startDate, 'day') && endDate.isSameOrAfter(usage.startDate, 'day'))) continue;
        if (countedMemberships.has(usage.memberShipId)) continue;

        countedMemberships.add(usage.memberShipId);

        let entry = membershipMap.get(usage.memberShipId);

        if (!entry) {
          entry = {
            membershipId: usage.memberShipId,
            membershipName: usage.memberShipName,
            count: 0,
            reRegisteredCount: 0,
            revenue: 0,
            ageCounts: new Array(10).fill(0),
          };
          membershipMap.set(usage.memberShipId, entry);
        }

        entry.count += 1;
        entry.ageCounts[ageIndex] += 1;
        // 첫 이용권(j===0)이 아니면 재등록 //
        if (j !== 0) entry.reRegisteredCount += 1;
      }
    }

    // 회원권별 매출 집계: 결제 상품명(itemName)이 회원권명과 일치하는 결제 합산 (환불 제외) //
    const revenueByName = new Map();

    for (const sale of salesData) {
      if (sale.isRefund) continue;

      const prevRevenue = revenueByName.get(sale.itemName) || 0;

      revenueByName.set(sale.itemName, prevRevenue + (sale.totalPrice || 0));
    }

    const memberships = Array.from(membershipMap.values());

    let totalMemberCount = 0;
    let totalMembershipRevenue = 0;

    for (const membership of memberships) {
      membership.revenue = revenueByName.get(membership.membershipName) || 0;
      totalMemberCount += membership.count;
      totalMembershipRevenue += membership.revenue;
    }

    // 인원수 내림차순 정렬 → 랭킹 //
    memberships.sort((a, b) => b.count - a.count || a.membershipId - b.membershipId);

    // 랭킹 행 + 연령대 세그먼트 가공 //
    const rankingList = memberships.map((membership) => {
      const segments = [];

      for (let i = 0; i < membership.ageCounts.length; i++) {
        if (membership.ageCounts[i] === 0) continue;

        segments.push({
          ageIndex: i,
          count: membership.ageCounts[i],
          percent: Math.round(membership.ageCounts[i] / membership.count * 100),
        });
      }

      return {
        membershipId: membership.membershipId,
        membershipName: membership.membershipName,
        count: membership.count,
        segments,
      };
    });

    // 상단 카드 + 평균 재등록률 산출 //
    let mostSelected = null;
    let topRevenue = null;
    let topReReg = null;
    let reRegRateSum = 0;
    let reRegRateCount = 0;

    for (const membership of memberships) {
      if (mostSelected === null || membership.count > mostSelected.count) mostSelected = membership;
      if (topRevenue === null || membership.revenue > topRevenue.revenue) topRevenue = membership;

      const reRegRate = membership.count > 0 ? membership.reRegisteredCount / membership.count : 0;

      reRegRateSum += reRegRate;
      reRegRateCount += 1;

      if (topReReg === null || reRegRate > topReReg.rate) topReReg = { membership, rate: reRegRate };
    }

    setRankings(rankingList);
    setTotalCount(totalMemberCount);
    setTotalRevenue(totalMembershipRevenue);
    setTopCards({
      mostSelected: mostSelected ? { name: mostSelected.membershipName, count: mostSelected.count } : null,
      topRevenue: topRevenue ? { name: topRevenue.membershipName, revenue: topRevenue.revenue } : null,
      topReReg: topReReg ? { name: topReReg.membership.membershipName, rate: topReReg.rate } : null,
    });
    setAvgReRegRate(reRegRateCount > 0 ? reRegRateSum / reRegRateCount : 0);
    setIsMembershipPreferenceLoading(false);
  }, [allMembers, salesData, startDate, endDate, isLoadingAllMember]);

  // 숫자 콤마 포맷팅 //
  function formatNumber(num) {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // 전체 대비 비율 계산 //
  function calcShare(value, total) {
    if (total === 0) return 0;

    return value / total * 100;
  }

  // 스택 바 호버 시 툴팁 표시 + 마우스 위치 추적 //
  function onMouseMoveBar(e, row) {
    setTooltipRow(row);
    setTooltipPosition({ x: e.clientX, y: e.clientY });
  }

  // 스택 바에서 벗어나면 툴팁 숨김 //
  function onMouseLeaveBar() {
    setTooltipRow(null);
  }

  // 툴팁 세그먼트를 3개씩 열로 분할 (3줄 초과 시 오른쪽으로 열 추가) //
  function chunkSegmentsToColumns(segments) {
    const columns = [];

    for (let i = 0; i < segments.length; i += 3) {
      columns.push(segments.slice(i, i + 3));
    }

    return columns;
  }

  // 화면 가장자리에서 툴팁이 잘리지 않도록 위치 보정 //
  function getTooltipStyle() {
    const isNearRight = tooltipPosition.x > window.innerWidth - 340;
    const isNearBottom = tooltipPosition.y > window.innerHeight - 200;

    return {
      left: tooltipPosition.x + (isNearRight ? -16 : 16),
      top: tooltipPosition.y + (isNearBottom ? -16 : 16),
      transform: 'translate(' + (isNearRight ? '-100%' : '0') + ', ' + (isNearBottom ? '-100%' : '0') + ')',
    };
  }

  // 상단 카드 렌더링 //
  function renderTopCard(icon, label, name, mainText, subText) {
    return (
      <TopCard>
        <TopCardLabelRow>
          {icon}
          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{label}</Text>
        </TopCardLabelRow>
        <Text fontFamily='NotoSansKR-Bold' fontSize={20} color={theme.text}>{name}</Text>
        <TopCardValueRow>
          <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.subText}>{mainText}</Text>
          <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.subText}>{subText}</Text>
        </TopCardValueRow>
      </TopCard>
    );
  }

  // 스택 바 세그먼트 렌더링 (10% 미만은 정보 생략하고 ...만 표시) //
  function renderSegment(segment, segmentIndex) {
    const segmentColor = theme[AGE_SEGMENT_COLOR_KEYS[segment.ageIndex]] || theme.brand700;
    const isTooNarrow = segment.percent < 10;

    return (
      <BarSegment key={'Segment-' + segmentIndex} $flex={segment.count} $color={segmentColor}>
        {
          isTooNarrow &&
          <SegmentLabel fontFamily='NotoSansKR-Bold' fontSize={9} color={theme.white}>...</SegmentLabel>
        }
        {
          !isTooNarrow &&
          <SegmentAgeLabel fontFamily='NotoSansKR-Bold' fontSize={9} color={theme.white}>
            {t('statistics.engagement.' + AGE_LABEL_KEYS[segment.ageIndex])}
          </SegmentAgeLabel>
        }
        {
          !isTooNarrow &&
          <SegmentValueWrapper>
            <SegmentLabel fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.white}>{segment.percent}</SegmentLabel>
            <SegmentLabel fontFamily='NotoSansKR-Medium' fontSize={9} color={theme.white}>%</SegmentLabel>
          </SegmentValueWrapper>
        }
      </BarSegment>
    );
  }

  // 회원권 랭킹 행 렌더링 //
  function renderRankingRow(row, index) {
    const badgeColor = index < 3 ? theme[RANK_BADGE_COLOR_KEYS[index]] : theme.thirdBackground;
    const badgeTextColor = index < 3 ? theme.white : theme.text;

    return (
      <RankingRow key={'MembershipRanking-' + index}>
        <RankBadge $bgColor={badgeColor}>
          <Text fontFamily='NotoSansKR-Bold' fontSize={16} color={badgeTextColor}>{index + 1}</Text>
        </RankBadge>
        <RankNameWrapper>
          <RankNameText fontFamily='NotoSansKR-Bold' fontSize={14} color={theme.text}>{row.membershipName}</RankNameText>
        </RankNameWrapper>
        <StackedBarTrack
          onMouseMove={(e) => onMouseMoveBar(e, row)}
          onMouseLeave={onMouseLeaveBar}>
          {row.segments.map((segment, segmentIndex) => renderSegment(segment, segmentIndex))}
        </StackedBarTrack>
        <RankCountWrapper>
          <Text fontFamily='NotoSansKR-Bold' fontSize={20} color={theme.text}>{formatNumber(row.count)}</Text>
          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{t('statistics.membershipPreference.memberUnit')}</Text>
        </RankCountWrapper>
      </RankingRow>
    );
  }

  // 상단 카드 데이터 텍스트 정리 //
  function getCardTexts() {
    const mostSelected = topCards?.mostSelected;
    const topRevenue = topCards?.topRevenue;
    const topReReg = topCards?.topReReg;

    return {
      mostSelected: {
        name: mostSelected ? mostSelected.name : '-',
        mainText: mostSelected ? formatNumber(mostSelected.count) + t('statistics.membershipPreference.memberUnit') : '-',
        subText: mostSelected ? t('statistics.membershipPreference.ofTotal', { percent: calcShare(mostSelected.count, totalCount).toFixed(1) }) : '',
      },
      topRevenue: {
        name: topRevenue ? topRevenue.name : '-',
        mainText: topRevenue ? Math.round(calcShare(topRevenue.revenue, totalRevenue)) + '%' : '-',
        subText: topRevenue ? t('statistics.membershipPreference.totalRevenue', { amount: formatNumber(topRevenue.revenue) }) : '',
      },
      topReReg: {
        name: topReReg ? topReReg.name : '-',
        mainText: topReReg ? Math.round(topReReg.rate * 100) + '%' : '-',
        subText: topReReg ? t('statistics.membershipPreference.avgReRegistration', { percent: Math.round(avgReRegRate * 100) }) : '',
      },
    };
  }

  const cardTexts = getCardTexts();

  return (
    <TopLevelWrapper $isDark={isDark}>
      {
        isMembershipPreferenceLoading ?
          <AnimationBox>
            <LottieWrapper>
              <Lottie animationData={LoadingAnimation} loop={true} />
            </LottieWrapper>
          </AnimationBox>
          :
          isOpenPartnerGymSales ?
            <ContentWrapper>
              <TitleRow>
                <Text fontFamily='NotoSansKR-Bold' fontSize={20} color={theme.text}>
                  {calcDateText()}
                </Text>
                <Text fontFamily='NotoSansKR-Bold' fontSize={20} color={theme.text}>
                  {t('statistics.membershipPreference.title')}
                </Text>
              </TitleRow>
              <TopCardRow>
                {renderTopCard(<StyledCrownIcon />, t('statistics.membershipPreference.mostSelected'), cardTexts.mostSelected.name, cardTexts.mostSelected.mainText, cardTexts.mostSelected.subText)}
                {renderTopCard(<StyledAddCardIcon />, t('statistics.membershipPreference.topRevenue'), cardTexts.topRevenue.name, cardTexts.topRevenue.mainText, cardTexts.topRevenue.subText)}
                {renderTopCard(<StyledCurrencyExchangeIcon />, t('statistics.membershipPreference.topReRegistration'), cardTexts.topReReg.name, cardTexts.topReReg.mainText, cardTexts.topReReg.subText)}
              </TopCardRow>
              <SubtitleRow>
                <Text fontFamily='NotoSansKR-Bold' fontSize={20} color={theme.text}>
                  {t('statistics.membershipPreference.selectedAmongJoined')}
                </Text>
                <Text fontFamily='NotoSansKR-Bold' fontSize={20} color={theme.text}>
                  {t('statistics.membershipPreference.totalMembers', { count: formatNumber(totalCount) })}
                </Text>
              </SubtitleRow>
              <RankingListWrapper>
                {rankings.map((row, index) => renderRankingRow(row, index))}
              </RankingListWrapper>
              {
                tooltipRow &&
                <TooltipWrapper style={getTooltipStyle()}>
                  <TooltipTitleBox>
                    <TooltipTitleText fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
                      {tooltipRow.membershipName}
                    </TooltipTitleText>
                  </TooltipTitleBox>
                  <TooltipColumns>
                    {
                      chunkSegmentsToColumns(tooltipRow.segments).map((column, columnIndex) => (
                        <TooltipColumn key={'TooltipColumn-' + columnIndex}>
                          {
                            column.map((segment) => (
                              <TooltipEntry key={'TooltipEntry-' + segment.ageIndex}>
                                <TooltipColorBox $color={theme[AGE_SEGMENT_COLOR_KEYS[segment.ageIndex]] || theme.brand700} />
                                <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.subText}>
                                  {t('statistics.engagement.' + AGE_LABEL_KEYS[segment.ageIndex])}
                                </Text>
                                <TooltipPercentWrapper>
                                  <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.subText}>{segment.percent}</Text>
                                  <Text fontFamily='NotoSansKR-Medium' fontSize={10} color={theme.subText}>%</Text>
                                </TooltipPercentWrapper>
                              </TooltipEntry>
                            ))
                          }
                        </TooltipColumn>
                      ))
                    }
                  </TooltipColumns>
                </TooltipWrapper>
              }
            </ContentWrapper>
            :
            <NotOpenWrapper>
              <Text fontFamily='NotoSansKR-Bold' fontSize={15} color={theme.subText}>{t('statistics.partnerGymPrivate')}</Text>
              <Text fontFamily='NotoSansKR-Regular' fontSize={15} color={theme.subText}>{t('statistics.partnerGymPrivateDesc')}</Text>
            </NotOpenWrapper>
      }
    </TopLevelWrapper>
  );
}

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;
  justify-content: center;

  margin: 39.5px 0;
  padding: 20px 40px;

  width: 100%;
  max-width: 960px;

  gap: 10px;

  border-radius: 20px;
  background-color: ${props => props.theme.subBackground};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;

  width: 100%;

  gap: 20px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;

  width: 100%;
`;

const TopCardRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  width: 100%;

  gap: 20px;

  opacity: 0.8;
`;

const TopCard = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;
  overflow: hidden;

  flex: 1;
  min-width: 0;

  padding: 15px;

  gap: 10px;

  border-radius: 10px;
  background-color: ${props => props.theme.thirdBackground};
`;

const TopCardLabelRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  gap: 10px;
`;

const TopCardValueRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  overflow: hidden;

  gap: 10px;
`;

const SubtitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;

  width: 100%;
`;

const RankingListWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;

  width: 100%;

  gap: 10px;
`;

const RankingRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  width: 100%;

  gap: 10px;
`;

const RankBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 25px;
  height: 25px;

  flex-shrink: 0;

  border-radius: 4px;
  background-color: ${props => props.$bgColor};
`;

const RankNameWrapper = styled.div`
  display: flex;
  align-items: center;
  overflow: hidden;

  width: 160px;

  flex-shrink: 0;
`;

const RankNameText = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  cursor: default;
`;

const StackedBarTrack = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  overflow: hidden;

  flex: 1;
  min-width: 0;

  height: 25px;

  border-radius: 5px;
  background-color: ${props => props.theme.buttonFill3};

  cursor: pointer;
`;

const BarSegment = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  justify-content: center;
  overflow: hidden;

  flex: ${props => props.$flex} 0 0;
  min-width: 40px;

  height: 100%;

  padding: 0 2px;

  gap: 5px;

  background-color: ${props => props.$color};
`;

const SegmentValueWrapper = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  gap: 2px;
`;

const SegmentLabel = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0;
  white-space: nowrap;
  cursor: pointer;
`;

const SegmentAgeLabel = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  cursor: pointer;
`;

const RankCountWrapper = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  justify-content: flex-end;
  overflow: hidden;

  width: 60px;

  flex-shrink: 0;

  gap: 2px;
`;

const StyledCrownIcon = styled(CrownSvg)`
  width: 24px;
  height: 24px;

  flex-shrink: 0;

  & path {
    fill: ${props => props.theme.brand500};
  }
`;

const StyledAddCardIcon = styled(AddCardSvg)`
  width: 24px;
  height: 24px;

  flex-shrink: 0;

  & path {
    fill: ${props => props.theme.brand500};
  }
`;

const StyledCurrencyExchangeIcon = styled(CurrencyExchangeSvg)`
  width: 24px;
  height: 24px;

  flex-shrink: 0;

  & path {
    fill: ${props => props.theme.brand500};
  }
`;

const AnimationBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 300px;
`;

const LottieWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 160px;
  height: 160px;
`;

const NotOpenWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  padding: 40px 0;
`;

const TooltipWrapper = styled.div`
  position: fixed;
  z-index: 1000;

  display: flex;
  flex-direction: column;

  align-items: flex-start;

  padding: 10px;

  gap: 8px;

  border-radius: 5px;
  background-color: ${props => props.theme.background};
  box-shadow: 0px 4px 50px 0px rgba(0, 0, 0, 0.05);

  pointer-events: none;
  white-space: nowrap;
`;

const TooltipTitleBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  width: 100%;

  padding: 4px 8px;

  border-radius: 2px;
  background-color: ${props => props.theme.buttonFill};
`;

const TooltipTitleText = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0;
  text-align: center;

  word-break: break-word;
  cursor: default;
`;

const TooltipColumns = styled.div`
  display: flex;
  flex-direction: row;

  align-items: flex-start;

  gap: 12px;
`;

const TooltipColumn = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;

  gap: 6px;
`;

const TooltipEntry = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  gap: 6px;
`;

const TooltipPercentWrapper = styled.div`
  display: flex;
  flex-direction: row;

  align-items: baseline;

  gap: 1px;
`;

const TooltipColorBox = styled.div`
  width: 12px;
  height: 12px;

  flex-shrink: 0;

  border-radius: 2px;
  background-color: ${props => props.$color};
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0;
  cursor: default;
`;
